// AdminSignInStrip — 2-step (PIN then password) admin login pill that
// lives in the /inventory hero banner. Persists the token in
// localStorage so a reload keeps admin mode active.

import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { API, NAVY, TEAL } from "./constants";

export function AdminSignInStrip({ store }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("pin");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [session, setSession] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const signOut = () => {
    localStorage.removeItem("pp_token");
    store.setToken("");
  };

  const submitPin = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/pp/auth/verify-pin`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin }),
      });
      const j = await r.json();
      if (!j.valid) throw new Error("Wrong PIN");
      setSession(j.session);
      setStep("password");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const submitPassword = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/pp/auth/verify-password`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session, password }),
      });
      const j = await r.json();
      if (!j.valid) throw new Error("Wrong password");
      localStorage.setItem("pp_token", j.token);
      store.setToken(j.token);
      setOpen(false); setPin(""); setPassword(""); setSession(""); setStep("pin");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (store.hasToken) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md font-semibold text-white" style={{ background: TEAL }}>
          <ShieldCheck className="h-3.5 w-3.5" /> Admin signed in
        </span>
        <button onClick={signOut} className="h-8 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="text-xs">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="sign-in-admin-btn" className="h-9 px-4 rounded-md text-xs font-semibold bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 shadow-sm" style={{ color: NAVY }}>
          <ShieldCheck className="h-3.5 w-3.5" /> Sign in as admin
        </button>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          {step === "pin" ? (
            <>
              <input autoFocus type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" data-testid="admin-pin-input" className="h-8 rounded-md border border-slate-300 text-slate-800 px-2 text-sm w-24" />
              <button onClick={submitPin} disabled={busy} data-testid="admin-pin-next-btn" className="h-8 px-3 rounded-md font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                Next
              </button>
            </>
          ) : (
            <>
              <input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" className="h-8 rounded-md border border-slate-300 text-slate-800 px-2 text-sm w-36" />
              <button onClick={submitPassword} disabled={busy} data-testid="admin-signin-btn" className="h-8 px-3 rounded-md font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                Sign in
              </button>
            </>
          )}
          <button onClick={() => { setOpen(false); setStep("pin"); setError(""); }} className="h-8 px-2 text-slate-500 hover:text-slate-800">
            Cancel
          </button>
          {error && <span className="text-red-600">{error}</span>}
        </div>
      )}
    </div>
  );
}
